import type React from "react"
import { useState, useEffect, useCallback } from "react"
import { Box, Container, Typography, Tabs, Tab, Paper, Badge, Alert, useTheme, useMediaQuery } from "@mui/material"
import RequestList from "../../../components/crm/request/RequestList"
import RequestCounters from "../../../components/crm/request/RequestCounters"
import { useAuth } from "../../../context/AuthContext"
import CustomAppBar from "../../../components/shared/CustomAppBar"
import { CustomDrawer } from "../../../components/shared/CustomDrawer"

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`request-tabpanel-${index}`}
      aria-labelledby={`request-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>{children}</Box>}
    </div>
  )
}

const RequestsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0)
  const { userToken } = useAuth()
  const [userProfileId, setUserProfileId] = useState<number | null>(null)
  const [userRole, setUserRole] = useState<string>("")
  const [counters, setCounters] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  })

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  // Usar useCallback para evitar recrear la función en cada renderizado
  const handleCountersUpdate = useCallback((total: number, pending: number, approved: number, rejected: number) => {
    // Verificar que los valores sean números válidos
    const validTotal = isNaN(total) ? 0 : total
    const validPending = isNaN(pending) ? 0 : pending
    const validApproved = isNaN(approved) ? 0 : approved
    const validRejected = isNaN(rejected) ? 0 : rejected

    setCounters({
      total: validTotal,
      pending: validPending,
      approved: validApproved,
      rejected: validRejected,
    })
  }, [])
  useEffect(() => {
    const profiles = userToken?.user.profiles || [];
    const activeProfile = profiles.find(profile => profile.roleId === 3 || profile.roleId === 7);

    console.log(activeProfile);

    if (activeProfile) {
      setUserProfileId(activeProfile.id)

      const role = activeProfile.roleName || ""
      setUserRole(role)
    } else {
      if (profiles && profiles.length > 0) {
        const fallbackProfile = profiles[0]
        setUserProfileId(fallbackProfile.id)
        const role = fallbackProfile.roleName || ""
        setUserRole(role)
      } else {
        console.error("No hay perfiles disponibles")
      }
    }
  }, [userToken])

  const profiles = userToken?.user.profiles || []

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  if (!userProfileId) {
    return (
      <>
      <CustomDrawer>
      <CustomAppBar />
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: "10vh" }, mb: { xs: 2, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Alert severity="warning">
          <Typography>Cargando información del usuario...</Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Detalles: profiles={JSON.stringify(profiles)}
          </Typography>
        </Alert>
      </Container>
      </CustomDrawer>
    </>
    )
  }

  return (
    <>
      <CustomDrawer>
      <CustomAppBar />
    <Container sx={{ mt: { xs: 2, md: "15vh" }, mb: { xs: 2, md: 4 }, px: { xs: 2, sm: 1, md: 1 } }}>
        <Typography 
            variant="h4" 
            component="h1"  
            sx={{
                fontWeight: 'bold',
                fontSize: '1.4rem',
                color: '#2c3e50', 
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                mb: 3, 
                fontFamily: 'Roboto, sans-serif', 
      
              }} gutterBottom>
              Solicitudes
        </Typography>

      <RequestCounters userProfileId={userProfileId} userRole={userRole} onCountersUpdate={handleCountersUpdate} />

      <Paper
        sx={{
          width: "100%",
          mb: 2,
          boxShadow: { xs: 1, md: 2 },
          borderRadius: { xs: 1, md: 2 },
          overflow: "hidden",
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : undefined}
          allowScrollButtonsMobile={isMobile}
          sx={{
            "& .MuiTab-root": {
              fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
              minWidth: { xs: 120, sm: 120 },
              px: { xs: 2, sm: 2 },
              py: 1.5,
            },
            "& .MuiTabs-scroller": {
              overflowX: "auto !important",
            }
          }}
        >
          <Tab
            label={
              <Badge
                badgeContent={counters.total}
                color="primary"
                showZero
                sx={{
                  '& .MuiBadge-badge': {
                    right: -10, 
                    fontSize: '0.65rem',
                    minWidth: '18px', 
                    height: '18px',
                  }
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", minWidth: { xs: 60, sm: 80 }, whiteSpace: "nowrap", }}>
                  <span>Todas</span>
                </Box>
              </Badge>

            }
          />
          <Tab
            label={
              <Badge badgeContent={counters.pending} 
                sx={{
                  '& .MuiBadge-badge': {
                    right: -4,
                    fontSize: '0.65rem',
                    minWidth: '18px',
                    height: '18px', 
                  }
                }}
                color="warning" showZero>
                <Box sx={{ display: "flex", alignItems: "center" , minWidth: { xs: 90, sm: 80 }, whiteSpace: "nowrap", }}>
                  <span>{isMobile ? "Pendientes" : "Pendientes"}</span>
                </Box>
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={counters.approved} 
                sx={{
                  '& .MuiBadge-badge': {
                    right: -4,
                    fontSize: '0.65rem',
                    minWidth: '18px',
                    height: '18px', 
                  }
                }}
                color="success" showZero>
                <Box sx={{ display: "flex", alignItems: "center" , minWidth: { xs: 90, sm: 80 }}}>
                  <span>Aprobadas</span>
                </Box>
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={counters.rejected} 
                sx={{
                  '& .MuiBadge-badge': {
                    right: -4,
                    fontSize: '0.65rem',
                    minWidth: '18px',
                    height: '18px', 
                  }
                }}
                color="error" showZero>
                <Box sx={{ display: "flex", alignItems: "center", minWidth: { xs: 90, sm: 80 } }}>
                  <span>Rechazadas</span>
                </Box>
              </Badge>
            }
          />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <RequestList userProfileId={userProfileId} userRole={userRole} statusFilter="all" />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <RequestList userProfileId={userProfileId} userRole={userRole} statusFilter="pending" />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <RequestList userProfileId={userProfileId} userRole={userRole} statusFilter="approved" />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <RequestList userProfileId={userProfileId} userRole={userRole} statusFilter="rejected" />
        </TabPanel>
      </Paper>
    </Container>
    </CustomDrawer>
    </>
  )
}

export default RequestsPage
